import { useEffect, useState, type CSSProperties } from 'react'
import { AnalyticsConsentCard } from './analytics/AnalyticsConsent'
import FramedProductImage from './components/FramedProductImage'
import ReviewCard, { type PublicReview } from './components/ReviewCard'
import { HomepageV3Footer, HomepageV3Header } from './components/HomepageV3Chrome'
import { cartBoxCount } from './order/cartStorage'
import { homepageV3, type HomepageV3Feature, type HomepageV3Product, type HomepageV3Step } from './config/homepageV3'

const assetRoot = '/assets/homepage-v3'

type HomepageDisplayProduct = HomepageV3Product & { imageAlt?: string }

export type HomepageRuntimeMenu = {
  supportingCopy: string
  products: readonly HomepageDisplayProduct[]
}

export type HomepageRuntimePricing = {
  prefix: string
  price: string
}

export type HomepageRuntimeContent = {
  heroHeadingLineOne: string
  heroHeadingLineTwo: string
  heroSupportingCopy: string
}

function Arrow() {
  return <span className="sg-v3-arrow" aria-hidden="true">→</span>
}

function FeatureIcon({ type }: { type: HomepageV3Feature['icon'] }) {
  if (type === 'oven') {
    return <svg viewBox="0 0 64 64" aria-hidden="true"><rect x="10" y="8" width="44" height="48" rx="5"/><path d="M10 22h44"/><circle cx="19" cy="15" r="1.5"/><circle cx="27" cy="15" r="1.5"/><rect x="18" y="31" width="28" height="16" rx="2"/><path d="M23 39h18"/></svg>
  }
  if (type === 'sparkle') {
    return <svg viewBox="0 0 64 64" aria-hidden="true"><path d="M31 7c2.2 12.2 8.6 18.6 21 21-12.4 2.4-18.8 8.8-21 21-2.4-12.2-8.8-18.6-21-21 12.2-2.4 18.6-8.8 21-21Z"/><path d="M50 8c.9 4.9 3.5 7.5 8 8.5-4.5 1-7.1 3.5-8 8.5-1-5-3.5-7.5-8-8.5 4.5-1 7-3.6 8-8.5Z"/></svg>
  }
  if (type === 'burst') {
    return <svg viewBox="0 0 64 64" aria-hidden="true"><path d="m32 6 5 14 13-7-7 13 15 4-15 4 7 13-13-7-5 15-5-15-13 7 7-13-15-4 15-4-7-13 13 7 5-14Z"/></svg>
  }
  if (type === 'fresh') {
    return <svg viewBox="0 0 64 64" aria-hidden="true"><path d="M17 40c0-13 10-24 28-27 1 18-8 32-23 32-8 0-13-5-13-12 0-6 4-12 11-17"/><path d="M17 48c7-11 15-19 28-27"/></svg>
  }
  if (type === 'truck') {
    return <svg viewBox="0 0 64 64" aria-hidden="true"><path d="M7 18h31v25H7z"/><path d="M38 27h10l9 10v6H38z"/><circle cx="18" cy="47" r="5"/><circle cx="48" cy="47" r="5"/></svg>
  }
  return <svg viewBox="0 0 64 64" aria-hidden="true"><path d="M32 54S9 41 9 24c0-8 5-13 13-13 5 0 8.7 2.5 10 7 1.7-4.5 5.5-7 10.5-7C50 11 55 16 55 24c0 17-23 30-23 30Z"/></svg>
}

function StepIcon({ type }: { type: HomepageV3Step['icon'] }) {
  if (type === 'menu') {
    return <svg viewBox="0 0 64 64" aria-hidden="true"><rect x="12" y="9" width="40" height="46" rx="5"/><path d="M21 21h22M21 31h22M21 41h14"/></svg>
  }
  if (type === 'oven') return <FeatureIcon type="oven" />
  return <FeatureIcon type="truck" />
}

function FactIcon({ type }: { type: string }) {
  if (type === 'location') {
    return <svg viewBox="0 0 48 48" aria-hidden="true"><path d="M24 43S9 30 9 18a15 15 0 1 1 30 0c0 12-15 25-15 25Z"/><circle cx="24" cy="18" r="5"/></svg>
  }
  if (type === 'calendar') {
    return <svg viewBox="0 0 48 48" aria-hidden="true"><rect x="7" y="10" width="34" height="31" rx="4"/><path d="M14 6v8M34 6v8M7 19h34"/></svg>
  }
  return <svg viewBox="0 0 48 48" aria-hidden="true"><circle cx="24" cy="24" r="18"/><path d="M24 14v11l7 5"/></svg>
}

function AccentLine({ text }: { text: string }) {
  const trimmed = text.trim()
  const splitAt = trimmed.lastIndexOf(' ')
  if (splitAt <= 0) return <strong>{trimmed}</strong>
  return <>{trimmed.slice(0, splitAt + 1)}<strong>{trimmed.slice(splitAt + 1)}</strong></>
}

function TopSection({ runtimeContent, dateRange }: { runtimeContent: HomepageRuntimeContent; dateRange: string | null }) {
  const top = homepageV3.top
  return (
    <section className="sg-v3-top" aria-labelledby="sg-v3-top-title">
      <div className="sg-v3-shell sg-v3-top__inner">
        <p className="sg-v3-eyebrow" aria-hidden={dateRange ? undefined : true}>{dateRange ?? '\u00A0'}</p>
        <h1 id="sg-v3-top-title">
          <span>{runtimeContent.heroHeadingLineOne}</span>
          <span><AccentLine text={runtimeContent.heroHeadingLineTwo} /></span>
        </h1>
        <p className="sg-v3-top__copy">{runtimeContent.heroSupportingCopy}</p>
        <div className="sg-v3-top__actions">
          <a className="sg-v3-button sg-v3-button--pink" href={top.primaryCta.href}>{top.primaryCta.label}<Arrow /></a>
          <a className="sg-v3-button sg-v3-button--outline" href={top.secondaryCta.href}>{top.secondaryCta.label}<Arrow /></a>
        </div>
      </div>
    </section>
  )
}

function ProductCard({ product }: { product: HomepageDisplayProduct }) {
  const style = {
    '--sg-v3-product-hover': product.hoverBackground,
  } as CSSProperties
  const soldOut = product.availability === 'sold-out'
  return (
    <a
      className={`sg-v3-product-card${soldOut ? ' is-sold-out' : ''}`}
      href={product.clickTarget}
      style={style}
      aria-label={`${product.name}, ${product.type}${soldOut ? ', sold out this week' : ''}`}
    >
      {soldOut ? <span className="sg-v3-sold-out">SOLD OUT</span> : null}
      {product.isNew && !soldOut ? <span className="sg-v3-new">NEW</span> : null}
      <div className="sg-v3-product-card__media">
        <FramedProductImage src={product.image} alt={product.imageAlt || product.name} framing={product.framing} />
      </div>
      <div className="sg-v3-product-card__caption">
        <h3>{product.name}</h3>
        <p>{product.type}</p>
        {product.description ? <span>{product.description}</span> : null}
      </div>
    </a>
  )
}

function WeeklyMenu({ runtimeMenu }: { runtimeMenu: HomepageRuntimeMenu }) {
  const products = [...runtimeMenu.products]
    .filter((product) => product.enabled)
    .sort((a, b) => a.displayOrder - b.displayOrder)
    .slice(0, 8)
  const firstSix = products.slice(0, 6)
  const finalTwo = products.slice(6, 8)
  const menu = homepageV3.weeklyMenu

  return (
    <section className="sg-v3-menu" id="weekly-menu" aria-labelledby="sg-v3-menu-title">
      <div className="sg-v3-shell">
        <div className="sg-v3-section-heading sg-v3-section-heading--center">
          <p className="sg-v3-eyebrow">{menu.eyebrow}</p>
          <h2 id="sg-v3-menu-title">{menu.heading}</h2>
          <p>{runtimeMenu.supportingCopy}</p>
        </div>
        <div className="sg-v3-product-grid">
          {firstSix.map((product) => <ProductCard product={product} key={product.id} />)}
        </div>
        {finalTwo.length ? (
          <div className="sg-v3-product-grid sg-v3-product-grid--final">
            {finalTwo.map((product) => <ProductCard product={product} key={product.id} />)}
          </div>
        ) : null}
      </div>
    </section>
  )
}

function Difference() {
  return (
    <section className="sg-v3-difference" id="difference" aria-labelledby="sg-v3-difference-title">
      <div className="sg-v3-shell">
        <div className="sg-v3-section-heading sg-v3-section-heading--center sg-v3-section-heading--dark">
          <p className="sg-v3-eyebrow">{homepageV3.difference.eyebrow}</p>
          <h2 id="sg-v3-difference-title">{homepageV3.difference.heading}</h2>
        </div>
        <div className="sg-v3-feature-grid">
          {homepageV3.difference.features.map((feature) => (
            <article className={`sg-v3-feature-card${feature.highlighted ? ' is-highlighted' : ''}`} key={feature.id}>
              <FeatureIcon type={feature.icon} />
              <h3>{feature.title}</h3>
              {feature.body ? <p>{feature.body}</p> : null}
            </article>
          ))}
        </div>
      </div>
    </section>
  )
}

function Reviews({ reviews }: { reviews: PublicReview[] }) {
  if (reviews.length !== 3) return null
  return (
    <section className="sg-v3-reviews" aria-labelledby="sg-v3-reviews-title">
      <div className="sg-v3-shell">
        <div className="sg-v3-reviews__heading">
          <p className="sg-v3-eyebrow">REVIEWS</p>
          <h2 id="sg-v3-reviews-title"><span>PEOPLE ARE</span><strong>SAYING THINGS.</strong></h2>
          <p>Real words from real Sugar Gremlin customers.<br />And yeah... it makes us smile.</p>
        </div>
        <div className="sg-v3-reviews__grid">
          {reviews.map((review) => <ReviewCard review={review} key={review.id} />)}
        </div>
        <div className="sg-v3-reviews__cta">
          <a className="sg-v3-button sg-v3-button--outline" href="/reviews">READ MORE REVIEWS <Arrow /></a>
        </div>
      </div>
    </section>
  )
}

function HowItWorks() {
  return (
    <section className="sg-v3-how" aria-labelledby="sg-v3-how-title">
      <div className="sg-v3-shell">
        <div className="sg-v3-section-heading sg-v3-section-heading--center">
          <p className="sg-v3-eyebrow">{homepageV3.howItWorks.eyebrow}</p>
          <h2 id="sg-v3-how-title">{homepageV3.howItWorks.heading}</h2>
        </div>
        <div className="sg-v3-timeline">
          <div className="sg-v3-timeline__line" aria-hidden="true" />
          {homepageV3.howItWorks.steps.map((step) => (
            <article className="sg-v3-step" key={step.number}>
              <div className="sg-v3-step__icon"><StepIcon type={step.icon} /></div>
              <span className="sg-v3-step__number">{step.number}</span>
              <h3>{step.title}</h3>
              {step.body ? <p>{step.body}</p> : null}
            </article>
          ))}
        </div>
        <div className="sg-v3-how__cta">
          <a className="sg-v3-button sg-v3-button--pink" href={homepageV3.howItWorks.cta.href}>{homepageV3.howItWorks.cta.label}<Arrow /></a>
        </div>
      </div>
    </section>
  )
}

function Delivery({ runtimePricing }: { runtimePricing: HomepageRuntimePricing }) {
  return (
    <section className="sg-v3-delivery" id="delivery" aria-labelledby="sg-v3-delivery-title">
      <div className="sg-v3-shell">
        <div className="sg-v3-section-heading">
          <p className="sg-v3-eyebrow">{homepageV3.delivery.eyebrow}</p>
          <h2 id="sg-v3-delivery-title">{homepageV3.delivery.heading}</h2>
        </div>
        <div className="sg-v3-delivery__layout">
          <div className="sg-v3-delivery__left">
            <div className="sg-v3-delivery-facts">
              {homepageV3.delivery.facts.map((fact) => (
                <article className="sg-v3-delivery-fact" key={fact.title}>
                  <span><FactIcon type={fact.icon} /></span>
                  <div><h3>{fact.title}</h3><p>{fact.body}</p></div>
                </article>
              ))}
            </div>
            <img className="sg-v3-delivery__van" src={`${assetRoot}/sg-home-delivery-van-approved.png`} alt="Sugar Gremlin delivery van" />
          </div>
          <aside className="sg-v3-delivery-card">
            <p className="sg-v3-eyebrow">UP TO 35 KM</p>
            <h3>{homepageV3.delivery.card.heading}</h3>
            <p>{homepageV3.delivery.card.supportingCopy}</p>
            <div className="sg-v3-delivery-card__price">
              <span>{runtimePricing.prefix}</span>
              <strong>{runtimePricing.price}</strong>
              <span>{homepageV3.delivery.card.priceSuffix}</span>
            </div>
            <p className="sg-v3-delivery-card__note">{homepageV3.delivery.card.note}</p>
            <a className="sg-v3-button sg-v3-button--pink" href="/order">Order Now<Arrow /></a>
          </aside>
        </div>
      </div>
    </section>
  )
}

export default function HomepageV3({
  weeklyMenu,
  runtimePricing,
  runtimeContent,
  featuredReviews = [],
  dateRange = null,
  previewMode = false,
}: {
  weeklyMenu: HomepageRuntimeMenu
  runtimePricing: HomepageRuntimePricing
  runtimeContent: HomepageRuntimeContent
  featuredReviews?: PublicReview[]
  dateRange?: string | null
  previewMode?: boolean
}) {
  const [cartCount, setCartCount] = useState(() => cartBoxCount())

  useEffect(() => {
    const refreshCart = () => setCartCount(cartBoxCount())
    window.addEventListener('storage', refreshCart)
    const timer = window.setInterval(refreshCart, 500)
    return () => {
      window.clearInterval(timer)
      window.removeEventListener('storage', refreshCart)
    }
  }, [])

  return (
    <div className="sg-v3">
      <a className="skip-link" href="#main-content">Skip to main content</a>
      <HomepageV3Header cartCount={cartCount} />
      <main id="main-content" tabIndex={-1}>
        <TopSection runtimeContent={runtimeContent} dateRange={dateRange} />
        <WeeklyMenu runtimeMenu={weeklyMenu} />
        <Difference />
        <Reviews reviews={featuredReviews} />
        <HowItWorks />
        <Delivery runtimePricing={runtimePricing} />
      </main>
      <HomepageV3Footer />
      {!previewMode && <AnalyticsConsentCard />}
    </div>
  )
}
